import React, { useEffect, useMemo, useState } from "react";
import { api, CATEGORY_LABELS, CATEGORY_ORDER, ASSETS } from "@/lib/api";
import { Search } from "lucide-react";
import SEO from "@/components/SEO";

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/menu")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (activeCat !== "all") out = out.filter((i) => i.category === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.description || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [items, activeCat, query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((it) => {
      g[it.category] = g[it.category] || [];
      g[it.category].push(it);
    });
    return g;
  }, [filtered]);

  return (
    <div data-testid="menu-page">
      <SEO
        title="La Carte"
        description="Carte du restaurant Globo Dancing à Trappes : entrées, plats traditionnels portugais (bacalhau à brás, polvo à lagareiro, francesinha), plats du jour, desserts maison (pastel de nata) et vins du Douro."
        path="/menu"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Menu",
          name: "Carte du Globo Dancing",
          description: "Cuisine portugaise traditionnelle à Trappes",
          inLanguage: "fr-FR"
        }}
      />
      {/* Hero */}
      <section className="pt-32 pb-12 azulejo-bg">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <div className="eyebrow">A nossa carta</div>
            <h1 className="text-5xl md:text-7xl mt-4 leading-[0.95]">
              La carte du <em className="italic text-[#C04E38]">Globo</em>
            </h1>
            <p className="text-[#5C5A56] text-lg mt-5 max-w-xl">
              Une cuisine portugaise authentique, des recettes de famille et des
              produits sélectionnés. Toutes nos préparations sont maison.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden h-72 md:h-96 reveal">
            <img src={ASSETS.bacalhau} alt="Spécialité" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[68px] z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-y border-[#E6E1D6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-2 px-2">
            {[{ k: "all", label: "Tout" }, ...CATEGORY_ORDER.map((k) => ({ k, label: CATEGORY_LABELS[k] }))].map((c) => (
              <button
                key={c.k}
                onClick={() => setActiveCat(c.k)}
                data-testid={`filter-${c.k}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-all ${
                  activeCat === c.k
                    ? "bg-[#C04E38] text-white shadow-md"
                    : "bg-white border border-[#E6E1D6] text-[#2C2B29] hover:border-[#C04E38]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative md:ml-auto md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A56]" />
            <input
              type="text"
              placeholder="Rechercher un plat..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="menu-search-input"
              className="input-warm !pl-9 !py-2.5 text-sm"
            />
          </div>
        </div>
      </section>

      {/* List */}
      <section className="py-16 md:py-24 azulejo-bg">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {loading && <p className="text-center text-[#5C5A56]">Chargement de la carte...</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-[#5C5A56]" data-testid="menu-empty">Aucun plat ne correspond à votre recherche.</p>
          )}
          {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
            <div key={cat} className="mb-20 last:mb-0" data-testid={`category-${cat}`}>
              <div className="flex items-baseline justify-between border-b border-dashed border-[#C04E38]/30 pb-4 mb-10">
                <h2 className="text-3xl md:text-5xl">{CATEGORY_LABELS[cat]}</h2>
                <span className="font-serif-display italic text-[#C04E38]">{grouped[cat].length} plats</span>
              </div>
              <ul className="grid md:grid-cols-2 gap-x-14 gap-y-8">
                {grouped[cat].map((it) => (
                  <li key={it.id} className="group" data-testid={`menu-item-${it.id}`}>
                    <div className="flex items-baseline gap-3">
                      <h3 className="text-xl md:text-2xl font-serif-display group-hover:text-[#C04E38] transition">{it.name}</h3>
                      <div className="flex-1 border-b border-dotted border-[#5C5A56]/30 mb-2" />
                      {it.price && <span className="font-serif-display text-lg md:text-xl text-[#C04E38] whitespace-nowrap">{it.price}</span>}
                    </div>
                    {it.description && (
                      <p className="text-[#5C5A56] mt-2 text-[15px] leading-relaxed">{it.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
